import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ChevronLeft, ArrowRight } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { useCart } from '../contexts/CartContext';
import { Button } from '../components/ui/button';
import { Separator } from '../components/ui/separator';
import { CheckoutSteps } from '../components/checkout/CheckoutSteps';
import { AddressForm } from '../components/checkout/AddressForm';
import { ShippingSelector } from '../components/checkout/ShippingSelector';
import { PaymentStep } from '../components/checkout/PaymentStep';
import { OrderSummary } from '../components/checkout/OrderSummary';
import { getAffiliateCode } from '../components/AffiliateTracker';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const CheckoutPage = () => {
  const { t, language, getLocalizedName } = useLanguage();
  const { isAuthenticated, token, user } = useAuth();
  const { cart, initialized: cartInitialized } = useCart();
  const navigate = useNavigate();

  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);

  const [shippingAddress, setShippingAddress] = useState({
    first_name: user?.first_name || '',
    last_name: user?.last_name || '',
    address_line1: '',
    address_line2: '',
    city: '',
    postal_code: '',
    country: 'FR',
    phone: ''
  });

  const [shippingMethod, setShippingMethod] = useState(null);
  const [relayPoint, setRelayPoint] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState('card');
  const [voucher, setVoucher] = useState(null);

  // Real-time carrier quotes
  const [carrierQuotes, setCarrierQuotes] = useState([]);
  const [quotesLoading, setQuotesLoading] = useState(false);
  const lastQuoteKey = useRef(null);

  const fetchCarrierQuotes = useCallback(async (country, items) => {
    if (!items || items.length === 0) return;
    const itemsKey = items.map((it) => `${it.product_id || ''}:${it.quantity}:${it.price}:${it.weight_kg || 0.5}`).join('|');
    const key = `${country || 'FR'}::${itemsKey}`;
    if (lastQuoteKey.current === key) return;
    lastQuoteKey.current = key;
    setQuotesLoading(true);
    try {
      const payload = {
        items: items.map((it) => ({
          price: it.price,
          quantity: it.quantity,
          weight_kg: it.weight_kg || 0.5,
        })),
        country: country || 'FR',
      };
      const { data } = await axios.post(`${API}/shipping/carriers`, payload);
      setCarrierQuotes(data?.quotes || []);
    } catch (err) {
      setCarrierQuotes([]);
    } finally {
      setQuotesLoading(false);
    }
  }, []);

  useEffect(() => {
    Promise.resolve().then(() => fetchCarrierQuotes(shippingAddress.country, cart.items));
  }, [shippingAddress.country, cart.items, fetchCarrierQuotes]);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login', { state: { from: { pathname: '/checkout' } } });
    } else if (cartInitialized && (!cart.items || cart.items.length === 0)) {
      navigate('/cart');
    }
  }, [isAuthenticated, cart, cartInitialized, navigate]);

  const selectedQuote = carrierQuotes.find((q) => q.carrier === shippingMethod) || carrierQuotes[0] || null;
  const shippingPrice = selectedQuote?.cost ?? 0;
  const total = cart.total + shippingPrice;
  const amountDue = total - (voucher?.discount || 0);

  const handleAddressChange = (e) => {
    setShippingAddress({ ...shippingAddress, [e.target.name]: e.target.value });
  };

  const validateAddress = () => {
    const required = ['first_name', 'last_name', 'address_line1', 'city', 'postal_code', 'country', 'phone'];
    for (const field of required) {
      if (!shippingAddress[field]) {
        toast.error(language === 'fr' ? 'Veuillez remplir tous les champs obligatoires' : 'Please fill all required fields');
        return false;
      }
    }
    return true;
  };

  const selectCarrier = (c) => {
    if (c !== shippingMethod) setRelayPoint(null);
    setShippingMethod(c);
  };

  const handleContinueToPayment = () => {
    if (validateAddress()) {
      if (selectedQuote?.type === 'relay' && !relayPoint) {
        toast.error(language === 'fr' ? 'Veuillez sélectionner un point relais' : 'Please select a pickup point');
        return;
      }
      setStep(2);
    }
  };

  const handlePlaceOrder = async () => {
    setLoading(true);
    try {
      const payload = {
        shipping_address: {
          ...shippingAddress,
          full_address: `${shippingAddress.address_line1}${shippingAddress.address_line2 ? ', ' + shippingAddress.address_line2 : ''}, ${shippingAddress.postal_code} ${shippingAddress.city}, ${shippingAddress.country}`,
        },
        shipping_method: shippingMethod,
        shipping_cost: shippingPrice,
        carrier: selectedQuote?.carrier || null,
        carrier_name: selectedQuote?.name || null,
        carrier_eta: selectedQuote?.eta_days || null,
        origin_url: window.location.origin,
        voucher_code: voucher?.code || null,
        affiliate_code: getAffiliateCode() || null,
        relay_point: selectedQuote?.type === 'relay' && relayPoint ? relayPoint : null,
      };

      const response = await axios.post(`${API}/payments/checkout/session`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.data?.url) {
        toast.success(language === 'fr' ? 'Redirection vers Stripe...' : 'Redirecting to Stripe...');
        window.location.href = response.data.url;
        return;
      }
      throw new Error('No checkout URL received');
    } catch (error) {
      toast.error(error.response?.data?.detail || (language === 'fr' ? 'Erreur lors du paiement' : 'Payment error'));
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="checkout-page">
      <div className="container-main py-8">
        <Button variant="ghost" onClick={() => step > 1 ? setStep(step - 1) : navigate('/cart')} className="mb-6">
          <ChevronLeft className="w-4 h-4 mr-1" />
          {step > 1 ? (language === 'fr' ? 'Retour' : 'Back') : t('cart')}
        </Button>

        <CheckoutSteps step={step} language={language} />

        <div className="grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            {step === 1 && (
              <div className="bg-white rounded-xl border border-border/40 p-6" data-testid="shipping-form">
                <AddressForm
                  address={shippingAddress}
                  onFieldChange={handleAddressChange}
                  onCountrySelect={(value) => setShippingAddress({ ...shippingAddress, country: value })}
                  language={language}
                  t={t}
                />

                <Separator className="my-6" />

                <ShippingSelector
                  quotes={carrierQuotes}
                  quotesLoading={quotesLoading}
                  shippingMethod={shippingMethod}
                  onSelectCarrier={selectCarrier}
                  selectedQuote={selectedQuote}
                  relayPoint={relayPoint}
                  onRelayChange={setRelayPoint}
                  address={shippingAddress}
                  language={language}
                />

                <Button onClick={handleContinueToPayment} className="w-full btn-primary h-12 mt-6" data-testid="continue-to-payment-btn" disabled={carrierQuotes.length === 0}>
                  {language === 'fr' ? 'Continuer vers le paiement' : 'Continue to Payment'}
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </div>
            )}

            {step === 2 && (
              <PaymentStep
                paymentMethod={paymentMethod}
                onPaymentMethodChange={setPaymentMethod}
                loading={loading}
                amountDue={amountDue}
                onPlaceOrder={handlePlaceOrder}
                language={language}
              />
            )}
          </div>

          <div className="lg:col-span-1">
            <OrderSummary
              cart={cart}
              voucher={voucher}
              onVoucherApplied={(v) => setVoucher(v)}
              onVoucherCleared={() => setVoucher(null)}
              selectedQuote={selectedQuote}
              shippingPrice={shippingPrice}
              amountDue={amountDue}
              language={language}
              t={t}
              getLocalizedName={getLocalizedName}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
